import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FormService {

  formState: any;

  form: UntypedFormGroup;

  private readonly ANNAT_LAND = '-1';
  private readonly JA = 'Ja';
  private readonly NEJ = 'Nej';

  get inkomsttypControl(): AbstractControl {
    return this.form.get('inkomsttyp');
  }

  get landControl(): AbstractControl {
    return this.form.get('land');
  }

  get isUtbetaldControl(): AbstractControl {
    return this.form.get('isUtbetald');
  }

  get utbetalningsdatumControl(): AbstractControl {
    return this.form.get('utbetalningsdatum');
  }

  get utbetalningsdatumArControl(): AbstractControl {
    return this.utbetalningsdatumControl.get('year');
  }

  get utbetalningsdatumManadControl(): AbstractControl {
    return this.utbetalningsdatumControl.get('month');
  }

  get utbetalningsdatumDagControl(): AbstractControl {
    return this.utbetalningsdatumControl.get('day');
  }

  get beloppUtbetaldControl(): AbstractControl {
    return this.form.get('beloppUtbetald');
  }

  get beloppUtbetaldCurrencyControl(): AbstractControl {
    return this.beloppUtbetaldControl.get('valuta');
  }

  get beloppUtbetaldAmountControl(): AbstractControl {
    return this.beloppUtbetaldControl.get('belopp');
  }

  get isUtlandskSkattControl(): AbstractControl {
    return this.form.get('isUtlandskSkatt');
  }

  get utlandskSkattControl(): AbstractControl {
    return this.form.get('utlandskSkatt');
  }

  get utlandskSkattCurrencyControl(): AbstractControl {
    return this.utlandskSkattControl.get('valuta');
  }

  get utlandskSkattAmountControl(): AbstractControl {
    return this.utlandskSkattControl.get('belopp');
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
  ) {
  }

  saveFormState(form: UntypedFormGroup): void {
    this.formState = form.getRawValue();
  }

  getFormState(): any {
    return this.formState;
  }


  getForm(inkomstar: string): UntypedFormGroup {
    let form: UntypedFormGroup;
    if (this.isEmpty(inkomstar)) {
      form = this.buildForm();
    } else {
      form = this.buildForm();
      this.utbetalningsdatumArControl.setValue(inkomstar);
    }

    return form;
  }

  buildForm(): UntypedFormGroup {
    this.form = null;
    this.form = this.fb.group({
      id: [null],
      inkomsttyp: [null],
      land: [null, [Validators.required]],
      isUtbetald: [null],
      valutadag: [null],
      bankdag: [null],
      utbetalningsdatum: this.fb.group({
        year: [null],
        month: [null],
        day: [null]
      }),
      beloppUtbetald: this.fb.group({
        valuta: [null],
        belopp: [null],
        valutakurs: [null]
      }),
      isUtlandskSkatt: [null],
      utlandskSkatt: this.fb.group({
        valuta: [null],
        belopp: [null],
        valutakurs: [null]
      })
    });

    return this.form;
  }

  isUtbetaldChanged(value: string, inkomstar: string, isEditMode: boolean): void {
    if (value === this.JA) {
      const formState = this.getFormState();
      if (this.isEmpty(formState)) {
        return;
      }
      formState.land = this.form.getRawValue().land;
      formState.isUtbetald = this.form.getRawValue().isUtbetald;
      if (isEditMode === false) {
        this.resetForm(inkomstar);
      }
      this.form.setValue(formState);
    } else if (value === this.NEJ) {
      this.saveFormState(this.form);
      this.resetControllers();
    }
  }

  arChanged() {
    // Endast om det finns ytterligare input sedan tidigare. Då nollställs allt.
    if (this.inkomsttypControl.value != null) {
      this.resetControllers();
      this.resetForm(this.utbetalningsdatumArControl.value);
    }
  }

  landChanged() {
    if (this.isAnnatLand()) {
      this.saveFormState(this.form);
      this.resetControllers();
      return;
    }

    const formState = this.getFormState();
    if (this.isEmpty(formState)) {
      return;
    }

    formState.land = this.form.getRawValue().land;

    this.resetForm(null);

    this.form.setValue(formState);
  }

  isAnnatLand(): boolean {
    return this.landControl.value === this.ANNAT_LAND;
  }

  isUtlandskSkattChanged(value: string): void {
    if (value === this.JA) {
      if (!this.utlandskSkattCurrencyControl.value) {
        this.utlandskSkattCurrencyControl.setValue(this.beloppUtbetaldCurrencyControl.value);
      }

      const formState = this.getFormState();
      if (this.isEmpty(formState)) {
        return;
      }

      formState.land = this.form.getRawValue().land;
      formState.utbetalningsdatum.day = this.form.getRawValue().utbetalningsdatum.day;
      formState.utbetalningsdatum.month = this.form.getRawValue().utbetalningsdatum.month;
      formState.utbetalningsdatum.year = this.form.getRawValue().utbetalningsdatum.year;
      formState.beloppUtbetald.belopp = this.form.getRawValue().beloppUtbetald.belopp;
      formState.beloppUtbetald.valuta = this.form.getRawValue().beloppUtbetald.valuta;
      formState.isUtlandskSkatt = this.form.getRawValue().isUtlandskSkatt;

      this.form.setValue(formState);
    } else if (value === this.NEJ) {
      this.saveFormState(this.form);
      this.resetControllers();
    }
  }

  resetControllers(): void {
    if (this.landControl.value === this.ANNAT_LAND) {
      this.isUtbetaldControl.reset();
      this.isUtlandskSkattControl.reset();
      this.beloppUtbetaldControl.reset();
      this.utbetalningsdatumManadControl.reset();
      this.utbetalningsdatumDagControl.reset();
      this.utlandskSkattControl.reset();
    } else if (this.isUtbetaldControl.value === this.NEJ) {
      this.utbetalningsdatumManadControl.reset();
      this.utbetalningsdatumDagControl.reset();
      this.beloppUtbetaldControl.reset();
      this.isUtlandskSkattControl.reset();
      this.utlandskSkattControl.reset();
    } else if (this.isUtlandskSkattControl.value === this.NEJ) {
      this.utlandskSkattControl.reset();
    }
  }

  resetForm(inkomstar: string): void {
    if (!this.isEmpty(inkomstar)) {
      this.resetFormWithExcludedFields(this.form, ['year']);
    } else {
      this.form.reset();
    }
    this.resetFormState();
  }

  resetFormWithExcludedFields(form: UntypedFormGroup, excludeFields: string[] = []): void {
    this.resetControls(form, excludeFields);
  }

  showSubmitBtn(): boolean {
    let valid = false;
    if (this.isUtlandskSkattControl.value === this.JA) {
      valid =
        !this.isControlEmpty(this.utbetalningsdatumDagControl) &&
        this.validAmount(this.beloppUtbetaldAmountControl) &&
        this.validAmount(this.utlandskSkattAmountControl) &&
        !this.isSameValutaAndSkattGreaterThanInkomst();
    } else {
      valid =
        !this.isControlEmpty(this.utbetalningsdatumDagControl) &&
        this.validAmount(this.beloppUtbetaldAmountControl) &&
        this.isUtlandskSkattControl.value === this.NEJ &&
        !this.isSameValutaAndSkattGreaterThanInkomst();
    }
    return valid;
  }

  validAmount(control: AbstractControl): boolean {
    return !this.isControlEmpty(control) && +this.removeSpaceChar(control.value) > 0;
  }

  isControlEmpty(control: AbstractControl): boolean {
    return this.isEmpty(control.value);
  }

  isEmpty(value: any): boolean {
    return value === undefined || value === null || value === '';
  }

  isSameValuta(): boolean {
    return this.utlandskSkattCurrencyControl.value === this.beloppUtbetaldCurrencyControl.value;
  }

  isSameValutaAndSkattGreaterThanHalfInkomst(): boolean {
    const isSameValuta = this.isSameValuta();
    const utlandskSkatt = +this.removeSpaceChar(this.utlandskSkattAmountControl.value);
    const beloppUtbetald = +this.removeSpaceChar(this.beloppUtbetaldAmountControl.value);
    const isSkattGreaterThanHalfInkomst = utlandskSkatt > beloppUtbetald * .5;
    return isSameValuta && isSkattGreaterThanHalfInkomst && this.beloppUtbetaldAmountControl.valid;
  }

  isSameValutaAndSkattGreaterThanInkomst(): boolean {
    const isSameValuta = this.isSameValuta();
    const utlandskSkatt = +this.removeSpaceChar(this.utlandskSkattAmountControl.value);
    const beloppUtbetald = +this.removeSpaceChar(this.beloppUtbetaldAmountControl.value);
    const isSkattGreaterThanInkomst = utlandskSkatt > beloppUtbetald;
    if (isSameValuta && isSkattGreaterThanInkomst) {
      this.beloppUtbetaldAmountControl.setErrors({incorrect: true});
      this.beloppUtbetaldAmountControl.markAsTouched();
    } else {
      this.beloppUtbetaldAmountControl.setErrors(null);
    }
    return isSameValuta && isSkattGreaterThanInkomst;
  }

  setFormValue(dittUnderlag: any) {
    this.form.setValue(dittUnderlag);
  }

  private resetControls(formGroup: UntypedFormGroup, excludeFields: string[]) {
    Object.keys(formGroup.controls).forEach(field => {

      const exists = excludeFields.find(key => key === field);
      if (exists) {
        return;
      }

      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.reset();
      } else if (control instanceof UntypedFormGroup) {
        this.resetControls(control, excludeFields);
      }
    });

    formGroup.markAsPristine();
    formGroup.markAsUntouched();
  }


  validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  enableAllControls(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.enable();
      } else if (control instanceof UntypedFormGroup) {
        this.enableAllControls(control);
      }
    });
  }

  isDirty(): boolean {
    return this.form.dirty;
  }

  private removeSpaceChar(str: string): string {
    return str === null ? '' : str.replace(/\s/g, '');
  }

  private resetFormState(): void {
    this.formState = null;
  }
}

export class FormServiceMock {
  createDittUnderlagForm() {
    return null;
  }

  validateAllFormFields(formGroup) {
    return null;
  }
}
